"use client";

import { useEffect, useRef, useState } from "react";
import { BrowserWindow, GradText, SectionHead, SectionShell } from "./kit";
import { useEnterStagger } from "./kit/motion";

/** Real app screenshot — set a path and it shows; while it's null (or the
 *  file 404s) the hand-built mock below renders instead. The previous
 *  /screenshots/dashboard.png showed the pre-redesign dashboard (a custom
 *  date-range picker and an "Unlock advanced analytics" upsell that the app
 *  doesn't have — analytics isn't plan-gated), so it was removed; drop in a
 *  current screenshot to bring the image back. */
const SCREENSHOT_SRC: string | null = null;

const NAV = [
  { icon: "▣", label: "Overview", on: true },
  { icon: "🔗", label: "Links" },
  { icon: "⑃", label: "Routing" },
  { icon: "▤", label: "QR Codes" },
  { icon: "◷", label: "Analytics" },
  { icon: "🌐", label: "Domains" },
  { icon: "⚙", label: "Settings" },
];

const STATS = [
  { label: "Human clicks · 7d", value: "128.4k", delta: "bots excluded" },
  { label: "Active links", value: "1,204", delta: "on 4 domains" },
  { label: "Mobile traffic", value: "62%", delta: "of human clicks" },
];

/** One bar per day — the mock labels the chart "Last 30 days" (a real dashboard period). */
const BARS = [
  38, 44, 41, 52, 47, 58, 40, 55, 48, 66, 61, 72, 58, 50, 63,
  80, 64, 75, 70, 88, 82, 70, 92, 85, 78, 90, 96, 84, 91, 98,
];

const ROWS = [
  { slug: "rii.link/sale", dest: "myshop.com/summer", clicks: "18,423", badge: "routing" },
  { slug: "rii.link/app", dest: "App Store / Play", clicks: "9,310", badge: "device" },
  { slug: "rii.link/menu", dest: "QR · restaurant", clicks: "4,206", badge: "QR" },
];

/** Placeholder rendering of the app until a real screenshot is provided. */
function DashboardMock() {
  return (
    <div className="grid min-h-[430px] grid-cols-[188px_1fr]">
      {/* sidebar */}
      <aside className="border-r border-line bg-surface-2 px-3 py-4">
        <div className="flex items-center gap-2 px-2 pb-3.5 pt-1 text-sm font-bold">
          <span className="grid h-5 w-5 place-items-center rounded-md bg-[linear-gradient(140deg,var(--color-brand-500),var(--color-brand-800))] text-xs text-white">
            r
          </span>
          rii.link
        </div>
        <nav className="flex flex-col gap-0.5">
          {NAV.map((n) => (
            <span
              key={n.label}
              className={`flex items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-[13px] font-medium ${
                n.on ? "bg-brand-600/[0.08] font-semibold text-brand-600" : "text-muted"
              }`}
            >
              <span className="w-4 text-center text-[12px]">{n.icon}</span>
              {n.label}
            </span>
          ))}
        </nav>
      </aside>

      {/* main */}
      <main className="px-5 py-[18px]">
        <div className="mb-4 flex items-center gap-3">
          <h5 className="text-base font-bold">Overview</h5>
          <span className="ml-auto rounded-lg bg-brand-600 px-3.5 py-[7px] text-xs font-semibold text-white">
            + New link
          </span>
        </div>

        <div className="mb-4 grid grid-cols-3 gap-3">
          {STATS.map((s) => (
            <div key={s.label} className="rounded-[11px] border border-line bg-surface px-3.5 py-[13px]">
              <div className="text-[11.5px] text-muted">{s.label}</div>
              <div className="mt-[3px] text-[21px] font-bold tracking-[-0.02em] tabular-nums">{s.value}</div>
              <div className="mt-0.5 font-jetbrains text-[11px] text-data-green">{s.delta}</div>
            </div>
          ))}
        </div>

        <div className="mb-4 rounded-[11px] border border-line bg-surface p-3.5">
          <div className="mb-3 flex items-center justify-between text-[12.5px] text-muted">
            <span>Requests over time</span>
            <span>Last 30 days</span>
          </div>
          <div className="flex h-[74px] items-end gap-[7px]">
            {BARS.map((h, i) => (
              <i
                key={i}
                className="flex-1 rounded-t-[4px] rounded-b-[2px] bg-[linear-gradient(180deg,var(--color-brand-500),var(--color-brand-600))]"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        </div>

        <div className="overflow-hidden rounded-[11px] border border-line bg-surface">
          <div className="grid grid-cols-[1.4fr_1fr_0.7fr_0.6fr] gap-2.5 bg-surface-2 px-3.5 py-[11px] text-[11px] font-semibold uppercase tracking-[0.04em] text-muted">
            <span>Short link</span>
            <span>Destination</span>
            <span>Clicks</span>
            <span>Status</span>
          </div>
          {ROWS.map((r) => (
            <div
              key={r.slug}
              className="grid grid-cols-[1.4fr_1fr_0.7fr_0.6fr] items-center gap-2.5 border-t border-line px-3.5 py-[11px] text-[12.5px]"
            >
              <span className="font-jetbrains font-semibold text-brand-600">{r.slug}</span>
              <span className="text-muted">{r.dest}</span>
              <span className="tabular-nums">{r.clicks}</span>
              <span className="justify-self-start rounded-md bg-data-green/[0.12] px-2 py-0.5 text-[11px] font-semibold text-data-green">
                {r.badge}
              </span>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

/**
 * Product dashboard shot in a BrowserWindow, on the section kit. SectionHead
 * self-choreographs; the window rises via useEnterStagger on the INNER
 * wrapper — never the [data-grid-block] itself (GridClearing measures blocks
 * with getBoundingClientRect).
 */
export default function DashboardSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [screenshotOk, setScreenshotOk] = useState(SCREENSHOT_SRC !== null);

  useEnterStagger(rootRef, "[data-dash-window]", { y: 36, duration: 1 });

  // If the SSR-rendered image already failed before hydration, onError never
  // fires — detect the broken state on mount and fall back to the mock.
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth === 0) setScreenshotOk(false);
  }, []);

  return (
    <SectionShell id="dashboard">
      <SectionHead
        eyebrow="The dashboard"
        title={
          <>
            One clean console for <GradText>every link you own.</GradText>
          </>
        }
        lead="Links, domains, routing rules and analytics live in a single fast interface — create a smart link, watch it perform, adjust the routing, all without leaving the page."
      />

      <div ref={rootRef}>
        <div data-grid-block className="relative">
          {/* Soft brand wash grounding the window on the grid paper. */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-x-4 -bottom-12 -top-8 -z-10 [background:radial-gradient(ellipse_65%_75%_at_50%_45%,rgba(215,14,98,0.08),transparent_70%)] md:-inset-x-10"
          />
          <div data-dash-window>
            <BrowserWindow url="my.rii.link/dashboard">
              {/* Fixed browser viewport — the app screenshot scrolls inside
                  it like a real page. Thin styled scrollbar so the
                  scrollability is visible without hovering. */}
              <div className="max-h-[460px] overflow-y-auto overscroll-contain [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-surface-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-surface-2 [&::-webkit-scrollbar-thumb]:bg-line-3 hover:[&::-webkit-scrollbar-thumb]:bg-brand-600/40">
                {screenshotOk ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    ref={imgRef}
                    src={SCREENSHOT_SRC ?? undefined}
                    alt="The rii.link dashboard — overview with click stats, traffic chart and link table"
                    className="block w-full select-none"
                    onError={() => setScreenshotOk(false)}
                  />
                ) : (
                  <DashboardMock />
                )}
              </div>
            </BrowserWindow>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
